import {
  customerActivityMetadata,
  getCustomerActivity,
} from "@redotech/redo-customer-segments-common/marketing/segments/customer-activity-metadata";
import {
  CustomerActivityType,
  CustomerDataSource,
  SegmentConditionCategory,
} from "@redotech/redo-customer-segments-common/marketing/segments/segment-types";
import CodeBrowserIcon from "@redotech/redo-icons/code-browser.svg";
import { filterTruthy } from "@redotech/util/array";
import {
  segmentCategoryToFriendly,
  segmentCategoryToIcon,
} from "../segment-category";
import { Category, Item } from "../trifold-selector";

export interface Activity extends Item {
  name: string;
  description: string;
  source: CustomerDataSource;
  type: CustomerActivityType;
}

export const CUSTOM_EVENT_ACTIVITY_TYPE = "custom_event" as const;

export interface CustomActivity extends Item {
  name: string;
  description: string;
  source: CustomerDataSource;
  type: typeof CUSTOM_EVENT_ACTIVITY_TYPE;
}

export type AnyActivity = Activity | CustomActivity;

export function isCustomActivity(
  activity: AnyActivity,
): activity is CustomActivity {
  return activity.type === CUSTOM_EVENT_ACTIVITY_TYPE;
}

export type ActivityCategory = Category<AnyActivity>;

function getItemsByCategory(
  category: SegmentConditionCategory,
  availableDataSourcesForOrders: CustomerDataSource[] = [
    CustomerDataSource.REDO,
    CustomerDataSource.SHOPIFY,
    CustomerDataSource.COMMENTSOLD,
  ],
  availableDataSourcesForMarketing: CustomerDataSource[] = [
    CustomerDataSource.REDO,
    CustomerDataSource.SHOPIFY,
    CustomerDataSource.COMMENTSOLD,
  ],
): Activity[] {
  const items = filterTruthy(
    Object.values(CustomerActivityType).map((type) => {
      if (customerActivityMetadata[type].category !== category) {
        return null;
      }
      const activity = getActivity(type);

      // Order-placed activities use enabled integrations (regardless of marketing settings)
      if (
        type === CustomerActivityType.ORDER_PLACED_SHOPIFY ||
        type === CustomerActivityType.ORDER_PLACED_COMMENTSOLD ||
        type === CustomerActivityType.ORDER_PLACED_BIGCOMMERCE
      ) {
        return availableDataSourcesForOrders.includes(activity.source)
          ? activity
          : null;
      }

      // Other CommentSold activities require marketing settings enabled
      if (activity.source === CustomerDataSource.COMMENTSOLD) {
        return availableDataSourcesForMarketing.includes(activity.source)
          ? activity
          : null;
      }

      // All other activities (Shopify non-order, REDO) use enabled integrations
      return availableDataSourcesForOrders.includes(activity.source)
        ? activity
        : null;
    }),
  );
  return items.sort((a, b) => a.name.localeCompare(b.name));
}

const customEventActivity: CustomActivity = {
  name: "Custom event",
  description:
    "Filter by a custom event you've sent via the Redo Custom Events API.",
  source: CustomerDataSource.REDO,
  type: CUSTOM_EVENT_ACTIVITY_TYPE,
};

export function getCategories(
  availableDataSourcesForOrders: CustomerDataSource[] = [
    CustomerDataSource.REDO,
    CustomerDataSource.SHOPIFY,
    CustomerDataSource.COMMENTSOLD,
  ],
  availableDataSourcesForMarketing: CustomerDataSource[] = [
    CustomerDataSource.REDO,
    CustomerDataSource.SHOPIFY,
    CustomerDataSource.COMMENTSOLD,
  ],
): ActivityCategory[] {
  return [
    {
      name: segmentCategoryToFriendly[SegmentConditionCategory.MARKETING],
      Icon: segmentCategoryToIcon[SegmentConditionCategory.MARKETING],
      items: getItemsByCategory(
        SegmentConditionCategory.MARKETING,
        availableDataSourcesForOrders,
        availableDataSourcesForMarketing,
      ),
    },
    {
      name: segmentCategoryToFriendly[SegmentConditionCategory.SHOPPING],
      Icon: segmentCategoryToIcon[SegmentConditionCategory.SHOPPING],
      items: getItemsByCategory(
        SegmentConditionCategory.SHOPPING,
        availableDataSourcesForOrders,
        availableDataSourcesForMarketing,
      ),
    },
    { name: "Custom", Icon: CodeBrowserIcon, items: [customEventActivity] },
  ];
}

export const categories = getCategories();

export function getActivity(type: CustomerActivityType): Activity {
  const { name, description, source } = getCustomerActivity(type);
  return { name, description, source, type };
}
